// Read-only view of the finance WebSocket's connection state, and the polling
// interval the finance lists fall back to when it is not carrying events.
//
// Separate from `useFinanceStream`, which is the singleton's DRIVER — mounted
// once in AppLayout, it opens the socket and dispatches events into React Query,
// the notification store and Sonner. This one only reads, so a component can
// subscribe without pulling the whole notification stack in with it.
import { useSyncExternalStore } from 'react';
import { financeStream, type FinanceStreamState } from '@/lib/finance-stream';

/**
 * How often a finance list re-asks the server for itself.
 *
 * The socket is the fast path; this is the floor under it. Before #292 there was
 * none at all — a socket that was refused, blocked by a proxy, or simply dropped
 * left the queue frozen until the operator pressed F5, with nothing on screen to
 * say so. A pending deposit sitting unseen is money the player is waiting on.
 *
 * `LIVE` is deliberately slow: on a healthy socket every arrival already
 * invalidates the list, so this only catches a socket that is open but no longer
 * being fed (a pod whose pg LISTEN died between heartbeats).
 */
export const LIVE_REFETCH_MS = 60_000;
export const OFFLINE_REFETCH_MS = 20_000;

/** Pure: which interval a given connection state deserves. */
export function financeRefetchInterval(state: FinanceStreamState): number {
  return state.status === 'live' ? LIVE_REFETCH_MS : OFFLINE_REFETCH_MS;
}

// Module-level so the references are stable across renders — `useSyncExternalStore`
// resubscribes whenever the subscribe function's identity changes.
const subscribe = (onChange: () => void) => financeStream.onState(onChange);
const getSnapshot = () => financeStream.getState();

export function useFinanceStreamState(): FinanceStreamState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useFinanceRefetchInterval(): number {
  return financeRefetchInterval(useFinanceStreamState());
}
